import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/feedback/EmptyState";
import { useApproveChangeRequest, useChangeRequests, useRejectChangeRequest } from "@/hooks/useSlots";

/** The pending slot-approval queue: promote a proposal or release its hold. */
export function SlotApprovalsPage() {
  const { t } = useTranslation();
  const approvals = useChangeRequests("pending");
  const approve = useApproveChangeRequest();
  const reject = useRejectChangeRequest();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("admin.pendingApprovals")}</h1>
        <p className="text-sm text-muted-foreground">
          Approving promotes the proposal to a live booking; rejecting releases its hold.
        </p>
      </div>

      {approvals.isLoading ? (
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">{t("common.loading")}</CardContent>
        </Card>
      ) : (approvals.data ?? []).length === 0 ? (
        <EmptyState title="No pending proposals" />
      ) : (
        <div className="space-y-3">
          {approvals.data?.map((request) => (
            <Card key={request.id}>
              <CardHeader>
                <CardTitle className="text-sm font-medium">
                  Load #{request.load_id} · Bay #{request.bay_id}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center justify-between gap-3">
                <span className="text-sm text-muted-foreground">
                  {new Date(request.start_utc).toLocaleString()} – {new Date(request.end_utc).toLocaleString()}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => void approve.mutate(request.id)}
                    disabled={approve.isPending}
                  >
                    <Check className="h-4 w-4" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => void reject.mutate(request.id)}
                    disabled={reject.isPending}
                  >
                    <X className="h-4 w-4" />
                    Reject
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
